/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import type { DocumentClassification, DocumentOwnerType, DocumentSummary } from '../../api/areas/accounts';
import { Callout, DataTable, FilterBar, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { RecordFilter } from '../../components/RecordFilter';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatCount, formatDate } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { useAccess } from '../../lib/access';
import { OwnerLink } from './OwnerLink';
import {
  CLASSIFICATION,
  DOC_VERIFICATION,
  EnumBadge,
  FilterSelect,
  formatBytes,
  Pager,
  paths,
  RefreshButton,
  ResetFilters,
  useEnumLabel,
  useListParams,
  useRecordName,
} from './shared';

const LIMIT = 50;
const OWNER_TYPES: readonly DocumentOwnerType[] = ['SUBJECT', 'DOSSIER', 'CUSTOMER', 'CASE'];
const DOCUMENT_TYPES = ['NATIONAL_ID_CARD', 'PASSPORT', 'BUSINESS_REGISTRATION_CERTIFICATE', 'REGISTRATION_DECLARATION', 'AUTHORIZATION_LETTER', 'OTHER'] as const;

/** `/documents`: document metadata. Content stays in encrypted object storage and is not served here. */
export function DocumentsPage() {
  const { t } = useTranslation('accounts');
  const label = useEnumLabel('accounts');
  const access = useAccess();
  useDocumentTitle(t('documents.title'));
  const id = useId();
  const list = useListParams(['owner_type', 'owner_id', 'document_type', 'classification'] as const);
  const { owner_type, owner_id, document_type, classification } = list.values;
  const result = useApi(
    (signal) =>
      api.listDocuments(
        {
          owner_type: (owner_type || undefined) as DocumentOwnerType | undefined,
          owner_id: owner_id || undefined,
          document_type: document_type || undefined,
          classification: (classification || undefined) as DocumentClassification | undefined,
          after: list.after || undefined,
          limit: LIMIT,
        },
        { signal },
      ),
    [owner_type, owner_id, document_type, classification, list.after],
  );

  const firstRow = result.status === 'success' ? result.data.items[0] : undefined;
  const knownOwner = firstRow && firstRow.owner_id === owner_id ? firstRow.owner_name : null;
  const lookupKind = owner_type === 'CUSTOMER' ? 'customer' : 'subject';
  const lookupId = owner_type === 'CUSTOMER' || owner_type === 'SUBJECT' ? owner_id : '';
  const lookedUp = useRecordName(lookupKind, lookupId, knownOwner);
  const ownerName = knownOwner ?? (lookupId ? lookedUp : owner_type ? label('ownerType', owner_type) : t('filter.selectedRecord'));

  const columns: Column<DocumentSummary>[] = [
    {
      key: 'document',
      header: t('documents.col.document'),
      className: 'whitespace-normal',
      cell: (d) => <EntityRef to={paths.document(d.id)} id={d.id} label={label('documentType', d.document_type)} className="min-w-40" />,
    },
    { key: 'owner', header: t('documents.col.owner'), className: 'min-w-32 whitespace-normal', cell: (d) => <OwnerLink type={d.owner_type} id={d.owner_id} name={d.owner_name} /> },
    { key: 'class', header: t('documents.col.classification'), cell: (d) => <EnumBadge group="classification" tones={CLASSIFICATION} value={d.classification} /> },
    {
      key: 'current',
      header: t('documents.col.current'),
      className: 'whitespace-normal',
      cell: (d) => (
        <div className="text-xs">
          {d.current_version_number ? t('documents.versionOf', { n: d.current_version_number, total: d.version_count }) : '—'}
          {d.mime_type ? (
            <div className="text-muted-foreground">
              {d.mime_type} · {formatBytes(d.size_bytes)}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'verification',
      header: t('documents.col.verification'),
      cell: (d) =>
        d.latest_verification_status ? <EnumBadge group="docVerification" tones={DOC_VERIFICATION} value={d.latest_verification_status} /> : <span className="text-muted-foreground">{t('common.none')}</span>,
    },
    { key: 'expires', header: t('documents.col.expires'), className: 'whitespace-nowrap', cell: (d) => formatDate(d.expires_at) },
    { key: 'dossiers', header: t('documents.col.dossiers'), numeric: true, cell: (d) => formatCount(d.binding_count) },
    { key: 'uploaded', header: t('documents.col.uploaded'), className: 'whitespace-nowrap', cell: (d) => formatDate(d.uploaded_at) },
  ];

  return (
    <>
      <PageHeader title={t('documents.title')} crumbs={[{ label: t('crumb') }]} subtitle={t('documents.subtitle')} actions={<RefreshButton onClick={result.reload} />} />
      <Callout tone="info" className="mb-4" title={t('document.encryptedTitle')}>
        {t('document.encrypted')}
      </Callout>
      <FilterBar actions={<ResetFilters show={list.filtered} onReset={list.reset} />}>
        <RecordFilter label={t('documents.col.owner')} placeholder={t('filter.search')} value={owner_id} selectedLabel={ownerName}
          search={async (query, signal) => {
            const [found, dossiers] = await Promise.all([
              api.search(query, { signal }),
              access.has(['dossier.read']) ? api.listDossiers({ domain: query, include_superseded: true, limit: 20 }, { signal }) : Promise.resolve({ items: [] }),
            ]);
            const records = found.results.filter((hit) => ['customer', 'subject', 'dossier'].includes(hit.kind) && hit.label)
              .map((hit) => ({ id: hit.id, label: hit.label!, detail: `${label('ownerType', hit.kind.toUpperCase())}${hit.detail ? ` · ${hit.detail}` : ''}`, kind: hit.kind.toUpperCase() }));
            records.push(...dossiers.items.map((d) => ({ id: d.id, label: d.unicode_name, detail: `${label('ownerType', 'DOSSIER')} · ${t('common.versionN', { n: d.dossier_version })}`, kind: 'DOSSIER' })));
            return [...new Map(records.map((r) => [`${r.kind}/${r.id}`, r])).values()];
          }}
          onPick={(item) => list.setFilters(item ? { owner_id: item.id, owner_type: item.kind ?? '' } : { owner_id: '' })} />
        <FilterSelect id={`${id}-o`} label={t('documents.owner')} value={owner_type} options={OWNER_TYPES.map((s) => [s, label('ownerType', s)] as const)} onChange={(v) => list.setFilters({ owner_type: v, owner_id: '' })} />
        <FilterSelect
          id={`${id}-t`}
          label={t('documents.documentType')}
          value={document_type}
          className="sm:w-56"
          options={DOCUMENT_TYPES.map((s) => [s, label('documentType', s)] as const)}
          onChange={(v) => list.setFilter('document_type', v)}
        />
        <FilterSelect
          id={`${id}-c`}
          label={t('documents.classification')}
          value={classification}
          options={Object.keys(CLASSIFICATION).map((s) => [s, label('classification', s)] as const)}
          onChange={(v) => list.setFilter('classification', v)}
        />
      </FilterBar>
      <Section flush>
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={5} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} title={t('documents.loadError')} />
          </div>
        ) : null}
        {result.status === 'success' ? (
          <>
            <DataTable columns={columns} rows={result.data.items} rowKey={(d) => d.id} empty={<EmptyState title={t('documents.empty')} />} />
            {result.data.items.length > 0 ? <Pager shown={result.data.items.length} hasPrev={Boolean(list.after)} nextAfter={result.data.next_after} onPrev={list.prev} onNext={list.next} /> : null}
          </>
        ) : null}
      </Section>
    </>
  );
}
